/**
 * Quantato Mascot
 * Cute Spud character with realistic, uncanny blinking eyes
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { EyeRenderer } from '../systems/EyeRenderer.js';
import { randomRange } from '../systems/Utils.js';

export class QuantatoMascot {
    constructor(scene, x, y, options = {}) {
        this.scene = scene;
        this.baseX = x;
        this.baseY = y;

        this.isUncanny = options.isUncanny !== false;
        this.scale = options.scale || 1;

        // Container for all mascot parts
        this.container = scene.add.container(x, y);

        // Create mascot components
        this.createBody();
        this.createEyes();
        this.createMouth();
        this.createArms();

        // Animation state
        this.bobTimer = 0;
        this.bobSpeed = 0.003;
        this.expressionTimer = 0;
        this.currentExpression = 'happy';

        // Dread state (for transition)
        this.dreadLevel = 0;
    }

    createBody() {
        // Main potato body (without eyes - we add realistic ones)
        this.body = this.scene.add.graphics();

        // Draw body shape
        this.body.fillStyle(CONSTANTS.COLORS.QUANTATO_HAPPY);
        this.body.fillEllipse(0, 10, 100 * this.scale, 120 * this.scale);

        // Highlight
        this.body.fillStyle(0xFFE066, 0.3);
        this.body.fillEllipse(0, 0, 80 * this.scale, 90 * this.scale);

        // Rosy cheeks
        this.body.fillStyle(CONSTANTS.COLORS.QUANTATO_CHEEK, 0.6);
        this.body.fillCircle(-34 * this.scale, 15 * this.scale, 15 * this.scale);
        this.body.fillCircle(34 * this.scale, 15 * this.scale, 15 * this.scale);

        this.container.add(this.body);
    }

    createEyes() {
        // Use EyeRenderer for realistic, uncanny eyes
        const eyeSize = 28 * this.scale;
        const eyeY = -15 * this.scale;
        const eyeSpacing = 38 * this.scale;

        this.leftEye = new EyeRenderer(this.scene, -eyeSpacing / 2, eyeY, {
            size: eyeSize,
            isUncanny: this.isUncanny,
            irisColor: CONSTANTS.COLORS.EYE_IRIS
        });

        this.rightEye = new EyeRenderer(this.scene, eyeSpacing / 2, eyeY, {
            size: eyeSize,
            isUncanny: this.isUncanny,
            irisColor: CONSTANTS.COLORS.EYE_IRIS
        });

        // Add eye containers to main container
        this.container.add(this.leftEye.container);
        this.container.add(this.rightEye.container);
    }

    createMouth() {
        this.mouth = this.scene.add.graphics();
        this.drawMouth('happy');
        this.container.add(this.mouth);
    }

    drawMouth(expression) {
        this.mouth.clear();

        const mouthY = 30 * this.scale;
        const mouthWidth = 50 * this.scale;

        switch (expression) {
            case 'happy':
                // Happy smile arc
                this.mouth.lineStyle(4 * this.scale, 0x333333);
                this.mouth.beginPath();
                this.mouth.arc(0, mouthY, mouthWidth / 2, 0.2, Math.PI - 0.2, false);
                this.mouth.strokePath();
                break;

            case 'surprised':
                // O shape
                this.mouth.fillStyle(0x333333);
                this.mouth.fillEllipse(0, mouthY + 5, 20 * this.scale, 25 * this.scale);
                break;

            case 'concerned':
                // Slight frown
                this.mouth.lineStyle(4 * this.scale, 0x333333);
                this.mouth.beginPath();
                this.mouth.arc(0, mouthY + 15, mouthWidth / 2, Math.PI + 0.3, -0.3, false);
                this.mouth.strokePath();
                break;

            case 'horror':
                // Twisted horror mouth
                this.mouth.lineStyle(5 * this.scale, 0x8B0000);
                this.mouth.beginPath();
                this.mouth.moveTo(-25 * this.scale, mouthY + 5);
                this.mouth.lineTo(-10 * this.scale, mouthY - 5);
                this.mouth.lineTo(0, mouthY + 10);
                this.mouth.lineTo(10 * this.scale, mouthY - 8);
                this.mouth.lineTo(25 * this.scale, mouthY + 5);
                this.mouth.strokePath();
                break;
        }
    }

    createArms() {
        this.leftArm = this.scene.add.graphics();
        this.rightArm = this.scene.add.graphics();

        this.drawArms();

        this.container.add(this.leftArm);
        this.container.add(this.rightArm);
    }

    drawArms(waveOffset = 0) {
        this.leftArm.clear();
        this.rightArm.clear();

        const armColor = CONSTANTS.COLORS.QUANTATO_HAPPY;

        // Left arm
        this.leftArm.lineStyle(8 * this.scale, armColor);
        this.leftArm.beginPath();
        this.leftArm.moveTo(-50 * this.scale, 10 * this.scale);
        this.leftArm.lineTo(-65 * this.scale, -15 * this.scale + waveOffset);
        this.leftArm.strokePath();

        // Right arm
        this.rightArm.lineStyle(8 * this.scale, armColor);
        this.rightArm.beginPath();
        this.rightArm.moveTo(50 * this.scale, 10 * this.scale);
        this.rightArm.lineTo(65 * this.scale, -15 * this.scale - waveOffset);
        this.rightArm.strokePath();
    }

    update(delta) {
        // Update eye animations
        this.leftEye.update(delta);
        this.rightEye.update(delta);

        // Gentle bobbing animation
        this.bobTimer += delta * this.bobSpeed;
        const bobOffset = Math.sin(this.bobTimer) * 3;
        this.container.y = this.baseY + bobOffset;

        // Arm wave animation
        const waveOffset = Math.sin(this.bobTimer * 2) * 5;
        this.drawArms(waveOffset);

        // Apply dread effects if active
        if (this.dreadLevel > 0) {
            this.applyDreadEffects();
        }

        // Random expression changes (uncanny effect)
        if (this.isUncanny) {
            this.expressionTimer += delta;
            if (this.expressionTimer > randomRange(5000, 10000)) {
                this.expressionTimer = 0;
                this.playMicroExpression();
            }
        }
    }

    playMicroExpression() {
        // Brief unsettling expression change
        const expressions = ['surprised', 'concerned'];
        const randomExpr = expressions[Math.floor(Math.random() * expressions.length)];

        this.drawMouth(randomExpr);

        // Return to normal after brief moment
        this.scene.time.delayedCall(150, () => {
            this.drawMouth('happy');
        });
    }

    // Set dread level (0-1) for transition effects
    setDread(level) {
        this.dreadLevel = Math.max(0, Math.min(1, level));

        // Update eye bloodshot based on dread
        this.leftEye.setBloodshot(level * 0.8);
        this.rightEye.setBloodshot(level * 0.8);

        // Dilate pupils with dread
        this.leftEye.setDilation(0.5 + level * 0.4);
        this.rightEye.setDilation(0.5 + level * 0.4);

        if (level > 0.7) {
            this.drawMouth('horror');
        } else if (level > 0.3) {
            this.drawMouth('concerned');
        }
    }

    applyDreadEffects() {
        // Body color shift toward horror
        const normalColor = CONSTANTS.COLORS.QUANTATO_HAPPY;
        const horrorColor = CONSTANTS.COLORS.QUANTATO_HORROR;

        // Interpolate colors (simplified)
        const r1 = (normalColor >> 16) & 0xFF;
        const g1 = (normalColor >> 8) & 0xFF;
        const b1 = normalColor & 0xFF;

        const r2 = (horrorColor >> 16) & 0xFF;
        const g2 = (horrorColor >> 8) & 0xFF;
        const b2 = horrorColor & 0xFF;

        const r = Math.floor(r1 + (r2 - r1) * this.dreadLevel);
        const g = Math.floor(g1 + (g2 - g1) * this.dreadLevel);
        const b = Math.floor(b1 + (b2 - b1) * this.dreadLevel);

        const blendedColor = (r << 16) | (g << 8) | b;

        // Redraw body with blended color
        this.body.clear();
        this.body.fillStyle(blendedColor);
        this.body.fillEllipse(0, 10, 100 * this.scale, 120 * this.scale);

        // Reduce cheek visibility
        this.body.fillStyle(CONSTANTS.COLORS.QUANTATO_CHEEK, 0.6 * (1 - this.dreadLevel));
        this.body.fillCircle(-34 * this.scale, 15 * this.scale, 15 * this.scale);
        this.body.fillCircle(34 * this.scale, 15 * this.scale, 15 * this.scale);
    }

    // Force both eyes to blink
    blink() {
        this.leftEye.forceBlink();
        this.rightEye.forceBlink();
    }

    // Make eyes look at a world position
    lookAt(worldX, worldY) {
        this.leftEye.lookAt(worldX, worldY);
        this.rightEye.lookAt(worldX, worldY);
    }

    // Get container position
    getPosition() {
        return { x: this.container.x, y: this.container.y };
    }

    setPosition(x, y) {
        this.baseX = x;
        this.baseY = y;
        this.container.x = x;
        this.container.y = y;
    }

    setDepth(depth) {
        this.container.setDepth(depth);
    }

    destroy() {
        this.leftEye.destroy();
        this.rightEye.destroy();
        this.container.destroy();
    }
}
